import { showNotifier, hideNotifier } from '@/utils/notifier';
import { urlencode, setDefaultSettings } from '@/utils/url';
import { date2str, str2date, addDays, dateRange } from '@/utils/format';
import { jsonCopy } from '@/utils/json';
import { debounce } from '@/utils/debounce';
import { uniqueSet } from '@/utils/uniqueSet';
import { calculateUntil } from '@/utils/slot-range';

export default {
    props: ['question', 'stateId'],
    data () {
        return {
            private_slotsRetries: this.$root.appConfig.slotsRetries,
            private_xhrs: [],
        };
    },
    watch: {
        dates () {
            this.private_slotsRetries = this.$root.appConfig.slotsRetries;
        },
        _datesWithNoData: {
            handler () {
                if (this._datesWithNoData.length && this.slots_data.slotCards && this.slots_data.slotCards.length) {
                    debounce(() => {
                        let dates = this._datesWithNoData.filter(date => !this.slots_daysFetching.includes(date));

                        if (dates.length) {
                            this._getSlots(dates[0], dates[dates.length - 1]);
                        }
                    }, this.$root.appConfig.xhrSlotsDebounce, true);
                }
            },
            immediate: true,
        },
    },
    computed: {
        slots_data () {
            return this.question.data;
        },
        _datesWithNoData () {
            if (!this.slots_data.effective.showDates) {
                return [];
            }

            return this.dates
                .concat(this.dates.map(date => date2str(addDays(str2date(date), this.dates.length))))
                .filter(date => !this.slots_datesWithData.includes(date))
                .filter(date => this.slots_data.availability.from ? str2date(date) > str2date(this.slots_data.availability.from) : true)
                .filter(date => this.slots_data.availability.to ? str2date(date) < str2date(this.slots_data.availability.to) : true);
        },
        slots_slotsFetching () {
            return this.private_xhrs.length !== 0;
        },
        slots_daysFetching () {
            let daysFetching = [];

            this.private_xhrs.forEach(descriptor => {
                daysFetching = daysFetching.concat(dateRange(descriptor.from, descriptor.until, false));
            });

            return uniqueSet(daysFetching.filter(date => !this.slots_datesWithData.includes(date)));
        },
        slots_datesWithData () {
            let datesWithData = [];

            if (!this.slots_data.effectivePeriod || this.slots_data.effectivePeriod.length === 0) {
                return datesWithData;
            }

            this.slots_data.effectivePeriod.forEach(period => {
                datesWithData = uniqueSet(datesWithData.concat(dateRange(period.from, period.until, true)));
            });

            return datesWithData;
        },
    },
    methods: {
        slots_setEffective (key, value) {
            this.$store.commit('flow/SET_QUESTION_DATA', {
                question: this.question,
                data: { ...this.question.data, effective: { ...this.question.data.effective, [key]: value } },
            });
        },
        slots_selectSlot (slot) {
            this.$emit('input', slot);
        },
        _showRetry (from, to) {
            showNotifier({
                stateId: this.stateId,
                icon: 'warning',
                title: this.$trl('aw.cant_get_slots'),
                buttons: [
                    {
                        title: this.$trl('aw.cant_get_slots.retry'),
                        handler: () => {
                            this.private_slotsRetries = this.private_slotsRetries || this.$root.appConfig.slotsRetries;
                            this._getSlots(from, to);
                            hideNotifier(this.stateId);
                        },
                    },
                ],
            });
        },
        _fetchSlots (options) {
            let { stateId, from, until, stage, onSuccess, onError, startFromStage } = options;

            let fetchingSlots = new XMLHttpRequest();

            let descriptor = { from, until };
            this.private_xhrs.push(descriptor);

            fetchingSlots.open('GET', `${this.$root.appConfig.apiUrl}/slots?${urlencode({
                from,
                to: until || date2str(addDays(str2date(until), 20)),
                stateId,
                stage,
                startFromStage,
                clinicId: this.$root.appConfig.clinicId,
                language: this.$root.appConfig.language,
                ...(window.__odrDebugIvrServer ? { ivrServer: window.__odrDebugIvrServer } : {}),
            })}`);

            fetchingSlots.onreadystatechange = () => {
                if (fetchingSlots.readyState !== 4) {
                    return;
                }

                this.private_xhrs.splice(this.private_xhrs.indexOf(descriptor), 1);

                if (fetchingSlots.status === 200) {
                    let response = JSON.parse(fetchingSlots.responseText);

                    let question = response.questions.find(question => ['providerslist', 'monthview'].includes(question.type));
                    if (question && question.data) {
                        onSuccess(question.data);
                    } else {
                        onError(response);
                    }
                } else {
                    let errorStep;

                    try {
                        errorStep = JSON.parse(fetchingSlots.responseText);
                    // eslint-disable-next-line no-empty
                    } catch (ignore) {}

                    onError(errorStep);
                }
            };

            setDefaultSettings(fetchingSlots, this.$root.appConfig);
            fetchingSlots.send();
        },
        _getSlots (from, to) {
            let until = date2str(addDays(str2date(to), 1));

            if (this.private_slotsRetries === 0) {
                this._showRetry(from, to);
                return;
            }

            this._fetchSlots({
                stateId: this.stateId,
                from,
                until,
                onSuccess: response => {
                    hideNotifier(this.stateId);

                    let oldHasPeriods = this.slots_datesWithData.join(',');

                    let newSlotsData = jsonCopy(this.slots_data);
                    const effectivePeriodUntil = response.effectivePeriod && response.effectivePeriod.length > 0 && response.effectivePeriod[0].until ? response.effectivePeriod[0].until : null;
                    const periodUntil = response.periods && response.periods.length > 0 && response.periods[0].until ? response.periods[0].until : null;

                    newSlotsData.effectivePeriod = newSlotsData.effectivePeriod.concat({
                        from: from,
                        until: calculateUntil(periodUntil, effectivePeriodUntil, until),
                    });

                    let newSlotCards = [];
                    (response.slotCards || []).forEach(card => {
                        let matchedCard = newSlotsData.slotCards.find(prepCard => prepCard.key === card.key);
                        if (!matchedCard) {
                            // Detect and retrieve new slotcards
                            newSlotCards.push(card);
                            return;
                        }

                        let cardSlots = card.slots.filter(slot => {
                            return !oldHasPeriods.split(',').includes(slot.datetime.split('T')[0]);
                        });

                        matchedCard.slots = matchedCard.slots.concat(cardSlots);

                        if (!matchedCard.nextAvailable) {
                            matchedCard.nextAvailable = [];
                        }

                        if (card.nextAvailable) {
                            matchedCard.nextAvailable = matchedCard.nextAvailable.concat(card.nextAvailable);
                        }
                    });

                    // Process for new slot cards received. Instead of ignore new slot cards, we append new cards to existing cards list,
                    // then checking for the new filter info existed and append new one to existing filters list
                    if (newSlotCards.length > 0) {
                        newSlotsData.slotCards = newSlotsData.slotCards.concat(newSlotCards);

                        // Get existing filters and filters received from response
                        const existingFilters = newSlotsData.filters || [];
                        const currentFilters = response.filters || [];

                        // Define a map of filters specific by filter id
                        const currentFilterMap = new Map();
                        const existingFilterMap = new Map();

                        existingFilters.forEach(filter => {
                            existingFilterMap.set(filter.id, filter);
                        });

                        currentFilters.forEach(filter => {
                            currentFilterMap.set(filter.id, filter);
                        });

                        newSlotCards.forEach(card => {
                            const filterProps = card.filterProperties || {};

                            // For each card filter, check for the new filter value received then put to the existing filter list if not exist.
                            for (const [key, values] of Object.entries(filterProps)) {
                                if (!values || values.length === 0) {
                                    continue;
                                }

                                const filterValue = values[0];
                                const currentFilter = currentFilterMap.get(key);
                                const existingFilter = existingFilterMap.get(key);

                                if (currentFilter && existingFilter) {
                                    const filterValueToAdd = currentFilter.values?.find(v => v.id === filterValue);
                                    const isFilterValueExisting = existingFilter.values?.some(v => v.id === filterValue);

                                    if (filterValueToAdd && !isFilterValueExisting) {
                                        existingFilter.values.push(filterValueToAdd);
                                        if (existingFilter.values.length > 1) {
                                            existingFilter.visible = true;
                                        }
                                    }
                                }
                            }
                        });
                    }

                    if ((response.availability.from &&
                         newSlotsData.availability.from &&
                         str2date(newSlotsData.availability.from) < str2date(response.availability.from)
                    ) || (response.availability.from && !newSlotsData.availability.from)
                    ) {
                        newSlotsData.availability.from = response.availability.from;
                    }

                    if ((response.availability.to &&
                         newSlotsData.availability.to &&
                         str2date(newSlotsData.availability.to) > str2date(response.availability.to)
                    ) || (response.availability.to && !newSlotsData.availability.to)
                    ) {
                        newSlotsData.availability.to = response.availability.to;
                    }

                    this.$store.commit('flow/SET_QUESTION_DATA', { question: this.question, data: newSlotsData });

                    if (this.slots_datesWithData.join(',') === oldHasPeriods) {
                        --this.private_slotsRetries;
                    } else {
                        this.private_slotsRetries = this.$root.appConfig.slotsRetries;
                    }
                },
                onError: errorStep => {
                    if (errorStep && errorStep.stateId) {
                        this.$store.dispatch('flow/ADD_STEP', { step: errorStep, appConfig: this.$root.appConfig });
                    } else {
                        let range = dateRange(from, to, false);
                        if (this.dates.find(date => range.includes(date))) {
                            --this.private_slotsRetries;
                            this._getSlots(from, to);
                        }
                    }
                },
            });
        },
    },
};
